/**
 * EditorSmoke — live particle layer over the writing canvas.
 *
 * Effects
 * ───────
 *  ink        — soft ink-smoke wisps rising (ours, kept)
 *  petals     — sakura petals drifting down (ATELIÊ petals, richer count)
 *  stardust   — twinkling gold sparkle stars (ATELIÊ stardust)
 *  inkrain    — ink drops falling with gravity and splash (ATELIÊ ink_rain)
 *  sparks     — high-voltage zigzag electric sparks (ATELIÊ sparks)
 *  glitch     — RGB-split neon glitch discharge (ATELIÊ neon)
 *  mist       — slow expanding nebula mist clouds (ATELIÊ mist)
 *  fire       — rising flame with colour ramp (WRITER'S fire)
 *  fireflies  — bioluminescent wandering dots (WRITER'S fireflies)
 *  neon       — electric cyan/magenta RGB-split sparks (WRITER'S neon)
 *  glyphs     — floating serif characters (WRITER'S glyphs)
 *  none       — disabled
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export type WritingEffect =
  | 'petals'
  | 'stardust'
  | 'inkrain'
  | 'sparks'
  | 'glitch'
  | 'mist'
  | 'fire'
  | 'fireflies'
  | 'neon'
  | 'glyphs'
  | 'none';

export interface SmokeHandle {
  emit: (viewportX: number, viewportY: number) => void;
}

interface Particle {
  kind: WritingEffect;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** Radius growth per unit — used only by ink. */
  grow: number;
  age: number;
  life: number;
  rot: number;
  vrot: number;
  /**
   * Multi-purpose number: 0|1 boolean flag for petals/sparks/neon;
   * HSL hue angle [52,84] for fireflies; float [0,1] for fire/glyphs.
   */
  hue: number;
  seed: number;
  // — optional fields used only by specific effects —
  /** mist: target expansion radius in device px */
  maxRadius?: number;
  /** glyphs: character to render via fillText */
  char?: string;
  /** stardust: sparkle oscillation frequency */
  pulseFreq?: number;
  /** glitch: colour channel index 0=cyan 1=magenta 2=green */
  colorIndex?: number;
  /** inkrain: per-particle gravity acceleration (device px · dt⁻¹) */
  g?: number;
}

export const EditorSmoke = forwardRef<SmokeHandle, { type?: WritingEffect; paper?: boolean }>(
  function EditorSmoke({ type = 'stardust', paper = false }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const particles = useRef<Particle[]>([]);
    const dprRef = useRef(1);
    const typeRef = useRef<WritingEffect>(type);
    typeRef.current = type;
    const paperRef = useRef(paper);
    paperRef.current = paper;

    useImperativeHandle(ref, () => ({
      emit(viewportX, viewportY) {
        const kind = typeRef.current;
        if (kind === 'none') return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = dprRef.current;
        const x = (viewportX - rect.left) * dpr;
        const y = (viewportY - rect.top) * dpr;
        if (x < 0 || y < 0 || x > canvas.width || y > canvas.height) return;

        const count =
          kind === 'petals'    ? 6  :
          kind === 'stardust'  ? 4  :
          kind === 'inkrain'   ? 5  :
          kind === 'sparks'    ? 9  :
          kind === 'glitch'    ? 7  :
          kind === 'mist'      ? 2  :
          kind === 'fire'      ? 22 :
          kind === 'fireflies' ? 3  :
          kind === 'neon'      ? 10 :
          kind === 'glyphs'    ? 3  : 3;

        for (let i = 0; i < count; i++) {
          const t = particles.current.length + i + Math.floor(viewportX + viewportY);
          // Mutating LCG — each rnd() call produces a DIFFERENT value.
          let s = (t * 2654435761) % 233280 + 1;
          const rnd = (m: number) => {
            s = (s * 9301 + 49297) % 233280;
            return (s / 233280) * m;
          };
          // Generic jitter used by ink + petals
          const jx = (rnd(10) - 5) * dpr;
          const jy = (rnd(6) - 3) * dpr;
          let p: Particle;

          switch (kind) {
            // ── PETALS (ATELIÊ) ──────────────────────────────────────────────
            case 'petals':
              p = {
                kind, x: x + jx, y: y + jy,
                vx: (rnd(1) - 0.5) * 0.5 * dpr,
                vy: (0.18 + rnd(0.3)) * dpr,
                r: (3 + rnd(2.5)) * dpr, grow: 0,
                age: 0, life: 1500 + rnd(1400),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.05,
                hue: rnd(1) < 0.4 ? 1 : 0, seed: t + i * 17,
              };
              break;

            // ── STARDUST (ATELIÊ) ────────────────────────────────────────────
            case 'stardust':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 12 * dpr,
                y: y + (rnd(1) - 0.5) * 6 * dpr,
                vx: (rnd(1) - 0.5) * 0.6 * dpr,
                vy: (0.12 + rnd(0.25)) * dpr,
                r: (0.7 + rnd(0.9)) * dpr, grow: 0,
                age: 0, life: 1300 + rnd(1000),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.04,
                hue: 0, seed: t + i * 19,
                pulseFreq: 0.008 + rnd(0.012),
              };
              break;

            // ── INKRAIN (ATELIÊ ink_rain) ─────────────────────────────────────
            case 'inkrain':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 12 * dpr,
                y: y - (5 + rnd(20)) * dpr,
                vx: (rnd(1) - 0.5) * 1.2 * dpr,
                vy: (1.2 + rnd(1.8)) * dpr,
                r: (1.4 + rnd(1.8)) * dpr, grow: 0,
                age: 0, life: 600 + rnd(350),
                rot: 0, vrot: 0,
                hue: 0, seed: t + i * 59,
                g: (0.15 + rnd(0.12)) * dpr,
              };
              break;

            // ── SPARKS (ATELIÊ) ───────────────────────────────────────────────
            case 'sparks':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 3 * dpr,
                y: y + (rnd(1) - 0.5) * 3 * dpr,
                vx: (rnd(1) - 0.5) * 9 * dpr,
                vy: (rnd(1) - 0.5) * 9 * dpr,
                r: (0.8 + rnd(1.2)) * dpr, grow: 0,
                age: 0, life: 150 + rnd(180),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.2,
                hue: 0, seed: t + i * 29,
              };
              break;

            // ── GLITCH (ATELIÊ neon) ──────────────────────────────────────────
            case 'glitch':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 4 * dpr,
                y: y + (rnd(1) - 0.5) * 4 * dpr,
                vx: (rnd(1) - 0.5) * 6 * dpr,
                vy: (rnd(1) - 0.5) * 6 * dpr,
                r: (1 + rnd(1.5)) * dpr, grow: 0,
                age: 0, life: 200 + rnd(200),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.1,
                hue: 0, seed: t + i * 13,
                colorIndex: Math.floor(rnd(3)),
              };
              break;

            // ── MIST (ATELIÊ) ─────────────────────────────────────────────────
            case 'mist':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 8 * dpr,
                y: y + (rnd(1) - 0.5) * 4 * dpr,
                vx: (rnd(1) - 0.5) * 0.4 * dpr,
                vy: -(0.15 + rnd(0.2)) * dpr,
                r: (6 + rnd(4)) * dpr, grow: 0,
                age: 0, life: 1100 + rnd(700),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.01,
                hue: 0, seed: t + i * 47,
                maxRadius: (22 + rnd(12)) * dpr,
              };
              break;

            // ── FIRE (WRITER'S) ───────────────────────────────────────────────
            case 'fire': {
              const isCore = rnd(1) < 0.35;
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 5 * dpr,
                y: y + (rnd(1) - 0.5) * 3 * dpr,
                vx: (rnd(1) - 0.5) * 0.7 * dpr,
                vy: -(1.4 + rnd(1.6)) * dpr,
                r: (isCore ? 3.5 + rnd(2.5) : 5 + rnd(4.5)) * dpr,
                grow: 0,
                age: 0, life: 300 + rnd(450),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.08,
                hue: rnd(1), seed: t + i * 113,
              };
              break;
            }

            // ── FIREFLIES (WRITER'S) ──────────────────────────────────────────
            case 'fireflies':
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 16 * dpr,
                y: y + (rnd(1) - 0.5) * 12 * dpr,
                vx: (rnd(1) - 0.5) * 0.3 * dpr,
                vy: -(0.18 + rnd(0.35)) * dpr,
                r: (1.2 + rnd(1.8)) * dpr, grow: 0,
                age: 0, life: 1300 + rnd(1000),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.02,
                // hue = HSLA angle in warm green-amber range [52, 84)
                hue: 52 + rnd(32), seed: t + i * 739,
              };
              break;

            // ── NEON (WRITER'S) ───────────────────────────────────────────────
            case 'neon':
              p = {
                kind, x, y,
                vx: (rnd(1) - 0.5) * 4.5 * dpr,
                vy: (rnd(1) - 0.5) * 4.5 * dpr,
                r: (1.0 + rnd(1.8)) * dpr, grow: 0,
                age: 0, life: 180 + rnd(240),
                rot: rnd(6.28), vrot: (rnd(1) - 0.5) * 0.35,
                hue: rnd(1) < 0.5 ? 0 : 1, seed: t + i * 97,
              };
              break;

            // ── GLYPHS (WRITER'S) ─────────────────────────────────────────────
            case 'glyphs': {
              const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$?*&§ø¶';
              p = {
                kind,
                x: x + (rnd(1) - 0.5) * 8 * dpr,
                y: y + (rnd(1) - 0.5) * 4 * dpr,
                vx: (rnd(1) - 0.5) * 0.9 * dpr,
                vy: -(0.7 + rnd(0.9)) * dpr,
                r: (8 + rnd(5)) * dpr, grow: 0,
                age: 0, life: 1300 + rnd(700),
                rot: (rnd(1) - 0.5) * 0.4, vrot: (rnd(1) - 0.5) * 0.04,
                hue: rnd(1), seed: t + i * 181,
                char: alphabet[Math.floor(rnd(1) * alphabet.length)],
              };
              break;
            }

            // Unknown / removed effect (e.g. legacy 'ink') → emit nothing.
            default:
              continue;
          }
          particles.current.push(p);
        }
        // Fire is dense (22/keystroke) — raise cap to 500.
        if (particles.current.length > 500) {
          particles.current.splice(0, particles.current.length - 500);
        }
      },
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;

      function resize() {
        if (!canvas) return;
        canvas.width = Math.max(1, canvas.clientWidth) * dpr;
        canvas.height = Math.max(1, canvas.clientHeight) * dpr;
      }
      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(canvas);

      let raf = 0;
      let last = 0;
      let running = true;

      function frame(now: number) {
        if (!running || !canvas || !ctx) return;
        if (!last) last = now;
        const dt = Math.min(48, now - last);
        last = now;
        const arr = particles.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (arr.length === 0) { raf = requestAnimationFrame(frame); return; }

        const onPaper = paperRef.current;

        for (let i = arr.length - 1; i >= 0; i--) {
          const p = arr[i];
          p.age += dt;
          if (p.age >= p.life) { arr.splice(i, 1); continue; }
          const k = p.age / p.life;
          const fade = Math.sin(Math.min(1, k) * Math.PI);

          // ── PETALS (ATELIÊ) ──────────────────────────────────────────────
          if (p.kind === 'petals') {
            p.vx += Math.sin((p.age + p.seed) / 260) * 0.012 * dpr;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            p.rot += p.vrot;
            const a = (onPaper ? 0.5 : 0.6) * fade;
            ctx.globalCompositeOperation = 'source-over';
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.fillStyle = p.hue ? `rgba(214,170,120,${a})` : `rgba(226,150,170,${a})`;
            ctx.beginPath();
            ctx.ellipse(0, 0, p.r, p.r * 0.5, 0, 0, 6.2832);
            ctx.fill();
            ctx.restore();

          // ── STARDUST (ATELIÊ) ────────────────────────────────────────────
          } else if (p.kind === 'stardust') {
            p.vx += Math.sin((p.age + p.seed) / 120) * 0.008 * dpr;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            p.rot += p.vrot;
            const sparkle = 0.4 + 0.6 * Math.sin(p.age * (p.pulseFreq ?? 0.01) + p.seed);
            const aDust = (onPaper ? 0.3 : 0.45) * fade * Math.max(0, sparkle);
            ctx.save();
            ctx.globalCompositeOperation = onPaper ? 'source-over' : 'lighter';
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            // seed is always an integer (t + i*19) so % 3 works cleanly
            const isStar = (p.seed % 3) === 0;
            if (isStar) {
              ctx.fillStyle = onPaper
                ? `rgba(130,95,20,${aDust})`
                : `rgba(255,225,150,${aDust})`;
              ctx.beginPath();
              ctx.moveTo(0, -p.r * 3.5);
              ctx.quadraticCurveTo(0, 0, p.r * 3.5, 0);
              ctx.quadraticCurveTo(0, 0, 0, p.r * 3.5);
              ctx.quadraticCurveTo(0, 0, -p.r * 3.5, 0);
              ctx.quadraticCurveTo(0, 0, 0, -p.r * 3.5);
              ctx.fill();
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(0, 0, Math.max(0.5, p.r * 0.5), 0, 6.2832);
              ctx.fill();
            } else {
              const rad = Math.max(1, p.r * 2);
              const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, rad);
              const cStr = onPaper ? '154,106,30' : '255,235,170';
              grad.addColorStop(0, `rgba(${cStr},${aDust})`);
              grad.addColorStop(0.5, `rgba(${cStr},${aDust * 0.4})`);
              grad.addColorStop(1, 'rgba(0,0,0,0)');
              ctx.fillStyle = grad;
              ctx.beginPath();
              ctx.arc(0, 0, rad, 0, 6.2832);
              ctx.fill();
            }
            ctx.restore();

          // ── INKRAIN (ATELIÊ ink_rain) ─────────────────────────────────────
          } else if (p.kind === 'inkrain') {
            p.vy += (p.g ?? 0.15 * dpr) * dt * 0.06;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            const aRain = (onPaper ? 0.85 : 0.7) * fade;
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = onPaper
              ? `rgba(38,32,26,${aRain})`
              : `rgba(150,180,220,${aRain})`;
            // Elegant teardrop geometry
            const tailLen = Math.min(25 * dpr, p.vy * 2.2);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y - tailLen);
            ctx.quadraticCurveTo(p.x - p.r, p.y, p.x, p.y + p.r);
            ctx.quadraticCurveTo(p.x + p.r, p.y, p.x, p.y - tailLen);
            ctx.fill();
            // Splat near end of life
            if (k > 0.8) {
              const splashAlpha = aRain * (1 - (k - 0.8) / 0.2);
              ctx.fillStyle = onPaper
                ? `rgba(38,32,26,${splashAlpha})`
                : `rgba(150,180,220,${splashAlpha})`;
              const splatR = p.r * (1.2 + (k - 0.8) * 5);
              ctx.beginPath();
              ctx.arc(p.x, p.y, splatR, 0, 6.2832);
              ctx.fill();
              const seedAngle = Math.sin(p.seed) * 10;
              for (let j = 0; j < 3; j++) {
                const angle = j * (Math.PI * 2 / 3) + seedAngle;
                const dist = splatR * (1.2 + (k - 0.8) * 8);
                const sx = p.x + Math.cos(angle) * dist;
                const sy = p.y + Math.sin(angle) * dist;
                const sr = Math.max(0.4 * dpr, p.r * 0.3 * (1.5 - k));
                ctx.beginPath();
                ctx.arc(sx, sy, sr, 0, 6.2832);
                ctx.fill();
              }
            }
            ctx.restore();

          // ── SPARKS (ATELIÊ) ───────────────────────────────────────────────
          } else if (p.kind === 'sparks') {
            p.vx *= Math.pow(0.88, dt * 0.06);
            p.vy *= Math.pow(0.88, dt * 0.06);
            const spx = p.x;
            const spy = p.y;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            const aSpark = fade * (onPaper ? 0.9 : 1.0);
            ctx.save();
            ctx.globalCompositeOperation = onPaper ? 'source-over' : 'lighter';
            ctx.strokeStyle = onPaper
              ? `rgba(180,130,0,${aSpark})`
              : `rgba(255,230,40,${aSpark})`;
            ctx.lineWidth = p.r * (onPaper ? 1.0 : 1.4);
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(spx, spy);
            const midX = (spx + p.x) / 2 + (Math.random() - 0.5) * 4 * dpr;
            const midY = (spy + p.y) / 2 + (Math.random() - 0.5) * 4 * dpr;
            ctx.lineTo(midX, midY);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            if (!onPaper && Math.random() > 0.4) {
              ctx.fillStyle = `rgba(255,255,255,${fade})`;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r * 0.6, 0, 6.2832);
              ctx.fill();
            }
            ctx.restore();

          // ── GLITCH (ATELIÊ neon) ──────────────────────────────────────────
          } else if (p.kind === 'glitch') {
            p.vx *= Math.pow(0.93, dt * 0.06);
            p.vy *= Math.pow(0.93, dt * 0.06);
            const gpx = p.x;
            const gpy = p.y;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            const glitchOn = Math.sin(p.age + p.seed) > 0.45;
            const offset = glitchOn ? (3 * dpr * (Math.random() > 0.5 ? 1 : -1)) : 0;
            const aGlitch = fade * (onPaper ? 0.8 : 1.0);
            let glitchColor = '';
            if (onPaper) {
              if (p.colorIndex === 0)      glitchColor = `rgba(190,15,50,${aGlitch})`;
              else if (p.colorIndex === 1) glitchColor = `rgba(15,60,180,${aGlitch})`;
              else                         glitchColor = `rgba(120,20,140,${aGlitch})`;
            } else {
              if (p.colorIndex === 0)      glitchColor = `rgba(0,255,235,${aGlitch})`;
              else if (p.colorIndex === 1) glitchColor = `rgba(255,0,180,${aGlitch})`;
              else                         glitchColor = `rgba(50,255,100,${aGlitch})`;
            }
            ctx.save();
            ctx.globalCompositeOperation = onPaper ? 'source-over' : 'lighter';
            ctx.strokeStyle = glitchColor;
            ctx.lineWidth = p.r;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(gpx + offset, gpy);
            ctx.lineTo(p.x + offset, p.y);
            ctx.stroke();
            if (glitchOn && !onPaper) {
              ctx.fillStyle = `rgba(255,255,255,${fade * 0.85})`;
              ctx.beginPath();
              ctx.arc(p.x + offset, p.y, Math.max(0.5, p.r * 0.5), 0, 6.2832);
              ctx.fill();
            }
            ctx.restore();

          // ── MIST (ATELIÊ) ─────────────────────────────────────────────────
          } else if (p.kind === 'mist') {
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            p.rot += p.vrot;
            const currentR = p.r + ((p.maxRadius ?? p.r * 3) - p.r) * k;
            const rClampM = Math.max(1, currentR);
            const baseAlpha = fade * (onPaper ? 0.05 : 0.09);
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            const mistGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rClampM);
            if (onPaper) {
              mistGrad.addColorStop(0, `rgba(205,195,180,${baseAlpha})`);
              mistGrad.addColorStop(0.5, `rgba(180,170,155,${baseAlpha * 0.5})`);
            } else {
              mistGrad.addColorStop(0, `rgba(180,195,215,${baseAlpha})`);
              mistGrad.addColorStop(0.5, `rgba(140,155,180,${baseAlpha * 0.5})`);
            }
            mistGrad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = mistGrad;
            ctx.beginPath();
            ctx.arc(p.x, p.y, rClampM, 0, 6.2832);
            ctx.fill();
            ctx.restore();

          // ── FIRE (WRITER'S) ───────────────────────────────────────────────
          } else if (p.kind === 'fire') {
            p.vx += Math.sin((p.age + p.seed) / 65) * 0.09 * dpr;
            p.vx *= 0.94;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            p.rot += p.vrot;
            const rNow = p.r * (1 - k * 0.72);
            const rClampF = Math.max(1, rNow);
            let fireColor = '';
            if (k < 0.22) {
              ctx.globalCompositeOperation = 'lighter';
              fireColor = onPaper
                ? `rgba(180,110,20,${0.95 * fade})`
                : `rgba(255,255,230,${0.9 * fade})`;
            } else if (k < 0.55) {
              ctx.globalCompositeOperation = 'lighter';
              fireColor = onPaper
                ? `rgba(140,60,20,${0.8 * fade})`
                : `rgba(255,150,20,${0.82 * fade})`;
            } else if (k < 0.82) {
              ctx.globalCompositeOperation = 'lighter';
              fireColor = onPaper
                ? `rgba(90,40,30,${0.55 * fade})`
                : `rgba(220,50,10,${0.65 * fade})`;
            } else {
              ctx.globalCompositeOperation = 'source-over';
              fireColor = onPaper
                ? `rgba(38,32,26,${0.3 * fade})`
                : `rgba(45,42,40,${0.28 * fade})`;
            }
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            const fireGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, rClampF);
            fireGrad.addColorStop(0, fireColor);
            fireGrad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = fireGrad;
            ctx.beginPath();
            ctx.arc(0, 0, rClampF * 1.5, 0, 6.2832);
            ctx.fill();
            ctx.restore();

          // ── FIREFLIES (WRITER'S) ──────────────────────────────────────────
          } else if (p.kind === 'fireflies') {
            p.vx += Math.sin((p.age + p.seed) / 450) * 0.016 * dpr;
            p.vy += Math.cos((p.age + p.seed) / 400) * 0.014 * dpr;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            const blink = (Math.sin((p.age + p.seed) / 180) + 1) / 2;
            const aFF = (onPaper ? 0.45 : 0.72) * fade * (0.28 + blink * 0.72);
            ctx.save();
            if (onPaper) {
              ctx.globalCompositeOperation = 'source-over';
              ctx.fillStyle = `rgba(134,116,40,${aFF})`;
              ctx.shadowColor = 'rgba(140,120,50,0.35)';
              ctx.shadowBlur = 3 * dpr;
            } else {
              ctx.globalCompositeOperation = 'lighter';
              // hue is a warm green-amber HSLA angle in [52,84]
              ctx.fillStyle = `hsla(${p.hue},90%,65%,${aFF})`;
              ctx.shadowColor = `hsla(${p.hue},90%,55%,0.6)`;
              ctx.shadowBlur = 6 * dpr;
            }
            ctx.beginPath();
            ctx.arc(p.x, p.y, Math.max(0.5, p.r), 0, 6.2832);
            ctx.fill();
            ctx.restore();

          // ── NEON (WRITER'S) ───────────────────────────────────────────────
          } else if (p.kind === 'neon') {
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            // Rapid air resistance — applied after position update so trace trails back
            p.vx *= 0.91;
            p.vy *= 0.91;
            const glitchActive = Math.random() < 0.15;
            const splitX = glitchActive ? (Math.random() * 8 - 4) * dpr : 0;
            const flicker = Math.random() < 0.1 ? 0.18 : 0.95;
            const aNeon = fade * flicker;
            ctx.save();
            ctx.lineWidth = p.r;
            ctx.lineCap = 'round';
            if (onPaper) {
              ctx.globalCompositeOperation = 'source-over';
              ctx.strokeStyle = p.hue === 0
                ? `rgba(190,25,100,${aNeon * 0.8})`
                : `rgba(20,110,185,${aNeon * 0.8})`;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p.x - p.vx * 1.8, p.y - p.vy * 1.8);
              ctx.stroke();
            } else {
              ctx.globalCompositeOperation = 'lighter';
              const cyan    = `rgba(0,240,255,${aNeon})`;
              const magenta = `rgba(255,10,150,${aNeon})`;
              const white   = `rgba(255,255,255,${aNeon})`;
              if (glitchActive) {
                ctx.strokeStyle = cyan;
                ctx.beginPath();
                ctx.moveTo(p.x + splitX, p.y);
                ctx.lineTo(p.x - p.vx * 2 + splitX, p.y - p.vy * 2);
                ctx.stroke();
                ctx.strokeStyle = magenta;
                ctx.beginPath();
                ctx.moveTo(p.x - splitX, p.y);
                ctx.lineTo(p.x - p.vx * 2 - splitX, p.y - p.vy * 2);
                ctx.stroke();
              } else {
                ctx.strokeStyle = p.hue === 0 ? cyan : magenta;
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p.x - p.vx * 2.2, p.y - p.vy * 2.2);
                ctx.stroke();
                ctx.strokeStyle = white;
                ctx.lineWidth = p.r * 0.5;
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p.x - p.vx * 1.1, p.y - p.vy * 1.1);
                ctx.stroke();
              }
            }
            ctx.restore();

          // ── GLYPHS (WRITER'S) ─────────────────────────────────────────────
          } else if (p.kind === 'glyphs') {
            p.vx += Math.sin((p.age + p.seed) / 160) * 0.012 * dpr;
            p.x += p.vx * dt * 0.06;
            p.y += p.vy * dt * 0.06;
            p.rot += p.vrot;
            const aGlyph = (onPaper ? 0.48 : 0.68) * fade;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            if (onPaper) {
              ctx.globalCompositeOperation = 'source-over';
              ctx.fillStyle = p.hue < 0.5
                ? `rgba(38,32,26,${aGlyph})`
                : `rgba(154,106,30,${aGlyph})`;
              ctx.font = `italic bold ${p.r}px Georgia, serif`;
            } else {
              ctx.globalCompositeOperation = 'lighter';
              ctx.fillStyle = p.hue < 0.5
                ? `rgba(152,168,215,${aGlyph})`
                : `rgba(227,180,65,${aGlyph})`;
              ctx.shadowColor = p.hue < 0.5
                ? 'rgba(150,166,214,0.4)'
                : 'rgba(223,201,147,0.4)';
              ctx.shadowBlur = 4 * dpr;
              ctx.font = `italic ${p.r}px Georgia, serif`;
            }
            ctx.fillText(p.char ?? 'A', 0, 0);
            ctx.restore();

          } else {
            // ── INK (ours, kept as-is) ────────────────────────────────────
            p.vy -= 0.0005 * dt * dpr;
            p.x += p.vx * dt * 0.06 + Math.sin((p.age + p.seed) / 200) * 0.22 * dpr;
            p.y += p.vy * dt * 0.06;
            p.r += p.grow * dt * 0.06;
            const a = (onPaper ? 0.18 : 0.24) * fade;
            ctx.globalCompositeOperation = onPaper ? 'source-over' : 'lighter';
            const rad = Math.max(1, p.r);
            const inkGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
            if (onPaper) {
              inkGrad.addColorStop(0, p.hue ? `rgba(154,106,30,${a})` : `rgba(38,32,26,${a})`);
              inkGrad.addColorStop(0.55, `rgba(38,32,26,${a * 0.35})`);
            } else {
              inkGrad.addColorStop(0, p.hue ? `rgba(223,201,147,${a})` : `rgba(150,166,214,${a})`);
              inkGrad.addColorStop(0.55, `rgba(90,104,150,${a * 0.35})`);
            }
            inkGrad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = inkGrad;
            ctx.beginPath();
            ctx.arc(p.x, p.y, rad, 0, 6.2832);
            ctx.fill();
          }
        }
        raf = requestAnimationFrame(frame);
      }
      raf = requestAnimationFrame(frame);

      return () => { running = false; cancelAnimationFrame(raf); ro.disconnect(); };
    }, []);

    return (
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 z-10 h-full w-full"
        aria-hidden
      />
    );
  },
);
